<template>
  <el-select v-model="value" @change="handleChange" :placeholder="t('setup.printers.usb.selectDevice')" :loading="loading">
    <el-option v-for="device in devices" :key="getIdentifier(device)" :label="getLabel(device)" :value="getIdentifier(device)" />
  </el-select>
</template>
<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getPlatform } from '@/utils/misc';
import { UsbPrinter, UsbDevice } from '@posx/capacitor-usb-printer';
import { getUsbDevices, refreshUsbListeners, setupUsbListeners } from '@/workers/usb.worker';
import { logError, logInfo, serializeError } from '@/utils/logger';

const { t } = useI18n();
const $props = defineProps<{ modelValue?: string; useDeviceName?: boolean }>();
const $emit = defineEmits(['change', 'update:modelValue']);
const value = ref($props.modelValue);
const devices = ref<UsbDevice[]>([]);
const loading = ref(false);
const useDeviceName = computed(() => $props.useDeviceName ?? false);

// portPath (e.g. "1-1.2") is stable across replug; deviceName (/dev/bus/usb/...) increments on each plug
const formatPortPath = (portPath: string) => portPath || 'Unknown Port';
const getIdentifier = (device: UsbDevice) => useDeviceName.value ? device.portPath : device.serialNumber;
const getLabel = (device: UsbDevice) => useDeviceName.value ? `${device.productName} (${formatPortPath(device.portPath)})` : `${device.productName} (${device.serialNumber})`;

const scanDevices = async () => {
  if (getPlatform() !== 'android') return;
  loading.value = true;
  try {
    await setupUsbListeners();
    await refreshUsbListeners();
    devices.value = getUsbDevices();
    logInfo('[UsbPrinter] listDevices result', { devices: devices.value });
  } catch (e) { logError('USB scan failed', serializeError(e)); }
  finally { loading.value = false; }
};

onMounted(scanDevices);
watch(() => $props.modelValue, (v) => { value.value = v; });

const handleChange = async () => {
  const device = devices.value.find(d => getIdentifier(d) === value.value);
  if (!device) return;
  try {
    const { granted } = await UsbPrinter.requestPermission({ deviceId: device.deviceId });
    if (!granted) { value.value = ''; $emit('change', ''); $emit('update:modelValue', ''); return; }
    await scanDevices();
    const updated = devices.value.find((d: UsbDevice) => d.deviceId === device.deviceId);
    const identifier = updated ? getIdentifier(updated) : value.value;
    value.value = identifier;
    $emit('change', identifier);
    $emit('update:modelValue', identifier);
  } catch (e) { logError('USB permission failed', serializeError(e)); }
};
</script>
